import { RefObject } from 'react';

import { DateTime } from 'luxon';

import { GenericWidgetProps } from '@/types';

export interface SensorData {
  date: string | number;

  [key: string]: number | string;
}

export enum BoxPlotCategory {
  Day = 'day',
  Sensor = 'sensor',
  Provider = 'provider',
}

export enum ChartType {
  LIVE = 'Live',
  HISTORICAL = 'Historical',
}

type CommonChartConfigType = {
  plotType: PlotType;
  title: string;
  type: ChartType;
  interval: ChartInterval;
};

export type BoxPlotChartBoxConfigType = {
  start?: DateTime;
  end?: DateTime;
  /** Sensor inventory node id, not the site. Mapped to measurement `inventoryId`. */
  sensorId: string;
  provider: string;
};

export type BoxPlotChartConfigType = CommonChartConfigType & {
  category: BoxPlotCategory;
  boxes: BoxPlotChartBoxConfigType[];
};

export type LinearChartConfigType = CommonChartConfigType & {
  from?: DateTime;
  to?: DateTime;
  plot: { value: string; label: string }[];
  sensors: { value: string; label: string }[];
  reference?: { label: string; value: number };
  /** How the series are drawn over time. 'points' hides the line but stays a 'line' series. */
  drawStyle?: LineDrawStyle;
  /** @deprecated Replaced by `drawStyle`; still read for widgets saved before the rename. */
  pointsOnly?: boolean;
};

export type ScatterPlotChartConfigType = CommonChartConfigType & {
  start?: DateTime;
  end?: DateTime;
  /** Sensor inventory node ids, not the site. Mapped to measurement `inventoryIdA` / `inventoryIdB`. */
  sensorIdA: string;
  sensorIdB: string;
  providerA: string;
  providerB: string;
};

export type HistogramChartConfigType = CommonChartConfigType & {
  start?: DateTime;
  end?: DateTime;
  /** Sensor inventory node id, not the site. Mapped to measurement `inventoryId`. */
  sensorId: string;
  provider: string;
};

export type AggregatedChartConfigType = LinearChartConfigType & {
  resolution: ChartAggregationResolution;
  windowSize?: string | undefined; // e.g., '3 days' for moving average
  // Optional custom colors for different providers or sensors. Keys are either provider or sensorId, and values are color strings.
  colors?: Record<string, string>;
  // Plot types drawn hidden at first; the legend toggles them on.
  hiddenPlotTypes?: string[];
};

export type ChartConfigType =
  | LinearChartConfigType
  | BoxPlotChartConfigType
  | ScatterPlotChartConfigType
  | HistogramChartConfigType
  | AggregatedChartConfigType;

export enum LineDrawStyle {
  LINE = 'line',
  POINTS = 'points',
}

export enum PlotType {
  LINEAR = 'Linear',
  BOXPLOT = 'BoxPlot',
  SCATTER = 'Scatter',
  HISTOGRAM = 'Histogram',
  AGGREGATED = 'Aggregated',
  XRANGE = 'xrange',
}

export type DialogMode = PlotType | 'ChartPicker';

export interface ChartConfigHandle {
  save: () => void;
}

export interface ChartProps<T extends ChartConfigType> extends GenericWidgetProps {
  id?: string;
  onOpenModal: () => void;
  config?: T;
  hideEditChart?: boolean;
}

export interface ChartConfigProps<T extends ChartConfigType> {
  config?: T;
  id: string;
  onSave: (id: string, config: T) => void;
  ref?: RefObject<ChartConfigHandle | null>;
  rootId?: string;
}

export interface AggregatedChartYAxisOptions {
  tickInterval?: number;
  minorTickInterval?: number;
  minRange?: number;
  floor?: number;
  ceiling?: number;
  /** Fraction of the axis range added above the data. `0` ends on the tick that covers the series. */
  maxPadding?: number;
}

export enum ChartInterval {
  XXShort = '5 minutes',
  ExtraShort = '15 minutes',
  Short = '30 minutes',
  Medium = '1 hours',
  Long = '6 hours',
  Day = '1 day',
  Week = '1 week',
  Month = '1 month',
  Year = '1 year',
}

export enum ChartAggregationResolution {
  DAILY = ChartInterval.Day, // '1 day',
  HOURLY = ChartInterval.Medium, // '1 hour',
  WEEKLY = ChartInterval.Week, // '1 week',
}

export const intervalToDuration = {
  [ChartInterval.XXShort]: { minutes: 5 },
  [ChartInterval.ExtraShort]: { minutes: 15 },
  [ChartInterval.Short]: { minutes: 30 },
  [ChartInterval.Medium]: { hours: 1 },
  [ChartInterval.Long]: { hours: 6 },
  [ChartInterval.Day]: { days: 1 },
  [ChartInterval.Week]: { weeks: 1 },
  [ChartInterval.Month]: { months: 1 },
  [ChartInterval.Year]: { years: 1 },
};

export interface ICardDataState {
  isLoading?: boolean;
  isError?: boolean;
  isEmpty?: boolean;
  /** The chart's data request is being retried because the API is rate limiting (HTTP 429). */
  isRateLimited?: boolean;
}
